import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  screeningAnswersComplete,
  type ScreeningQuestionPublic,
} from "@/lib/candidateScreening";
import { cn } from "@/lib/utils";

/** EP-20 — the recruiter's knock-off questions, the LAST step before /start:
 *  after consent (so the attempt we write is lawful), before the camera,
 *  fullscreen and the clock (so a candidate who is screened out never grants a
 *  camera or loses test time). Answers go to /start with the rest of the form;
 *  the server alone decides. Options keep the recruiter's order — screening
 *  answers are usually ordinal, and there is no correct position to hide.
 *
 *  Nothing here knows which answers end the session: the candidate copy of a
 *  question (`ScreeningQuestionPublic`) carries no key. */
export function ScreeningStep({
  questions,
  answers,
  onAnswer,
  onBack,
  onContinue,
  submitting,
  error,
}: {
  questions: ScreeningQuestionPublic[];
  answers: Record<string, string>;
  onAnswer: (key: string, value: string) => void;
  onBack: () => void;
  onContinue: () => void;
  submitting: boolean;
  error: string | null;
}) {
  const complete = screeningAnswersComplete(questions, answers);
  return (
    <div className="flex min-h-full items-center justify-center bg-background p-6">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Before you start
          </p>
          <CardTitle as="h1" className="text-2xl">
            A few quick questions
          </CardTitle>
          <CardDescription>
            The recruiter asks every candidate these before the test. The timer
            hasn&rsquo;t started yet.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {questions.map((q, qIdx) => {
            const promptId = `screening-${q.key}-prompt`;
            return (
              <div key={q.key} className="space-y-3">
                <p id={promptId} className="text-sm font-medium text-foreground">
                  <span className="mr-1 font-mono text-xs text-muted-foreground">
                    {qIdx + 1}.
                  </span>
                  {q.prompt}
                </p>
                <RadioGroup
                  aria-labelledby={promptId}
                  value={answers[q.key] ?? ""}
                  onValueChange={(v) => onAnswer(q.key, v)}
                  className="grid gap-2"
                  disabled={submitting}
                >
                  {q.options.map((opt, oIdx) => {
                    const id = `screening-${q.key}-${oIdx}`;
                    const selected = answers[q.key] === opt;
                    return (
                      <Label
                        key={id}
                        htmlFor={id}
                        className={cn(
                          "flex min-h-11 cursor-pointer items-center gap-3 rounded-md border px-4 py-2.5 font-normal transition-colors",
                          "hover:bg-accent",
                          selected ? "border-primary bg-primary/5" : "border-border",
                        )}
                      >
                        <RadioGroupItem id={id} value={opt} />
                        <span className="text-sm leading-snug">{opt}</span>
                      </Label>
                    );
                  })}
                </RadioGroup>
              </div>
            );
          })}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </CardContent>
        <CardFooter className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            className="w-full min-h-11 cursor-pointer sm:w-auto"
            onClick={onBack}
            disabled={submitting}
          >
            Back
          </Button>
          <Button
            type="button"
            className="w-full min-h-11 cursor-pointer sm:flex-1"
            disabled={!complete || submitting}
            onClick={onContinue}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Checking…
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

/** EP-20 — the end of the road for a screened-out candidate. The message is
 *  the server's (one fixed, neutral string — ⚠ final wording owed to Emilia);
 *  it never names the question or the answer. No retry: the link is spent and
 *  a second go with other answers is exactly what screening refuses. */
export function ScreeningDeclinedCard({ message }: { message: string }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-background p-6">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-2">
          <CardTitle as="h1" className="text-2xl">
            Thank you
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p role="status" className="text-sm leading-relaxed text-foreground/90">
            {message}
          </p>
        </CardContent>
        <CardFooter>
          <p className="text-xs text-muted-foreground">
            You can close this page.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
